'use client';

import React, { useState } from 'react';
import { calculateNumerology, NumerologyResponse } from '@/lib/api/client';
import { NumerologyResultCard } from '@/components/numerology/NumerologyResultCard';
import { CalculatorPageContent } from '@/components/calculators/CalculatorPageContent';
import { Calendar, Sparkles } from 'lucide-react';

export default function NumerologyCalculatorPage() {
  const [dateOfBirth, setDateOfBirth] = useState('1990-05-15');
  const [result, setResult] = useState<NumerologyResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data = await calculateNumerology({ dateOfBirth });
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to calculate Life Path & Birthday numbers');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const introParagraph = "Pythagorean Numerology translates your date of birth into core vibrational numbers. Your Life Path Number represents your central life lesson and journey, while your Birthday Number highlights key natural talents.";

  const methodologyContent = (
    <div className="space-y-3">
      <p>
        I calculate your Life Path Number by reducing the digits of your birth day, birth month, and birth year individually before summing them, preserving Master Numbers (11, 22, 33) without reducing them to single digits.
      </p>
    </div>
  );

  const subsections = [
    {
      title: "The Life Path Number Concept",
      content: "Derived from your full birth date, your Life Path Number is considered the single most significant number in western Pythagorean numerology—mapping your core path, challenges, and opportunities."
    },
    {
      title: "Master Numbers (11, 22, 33)",
      content: "When digit sums result in 11, 22, or 33, they are preserved as Master Numbers. These carry heightened potential for intuition (11), master building (22), and spiritual teaching (33)."
    },
    {
      title: "Pythagorean Reduction Rules",
      content: "Classical Pythagorean reduction sums digits iteratively until a single digit (1-9) or Master Number is achieved, maintaining mathematical symmetry across calendar dates."
    }
  ];

  const faqs = [
    {
      q: "What is a Life Path Number?",
      a: "Your Life Path Number is the sum of your full birth date reduced to a single digit or Master Number (11, 22, 33), representing your life purpose."
    },
    {
      q: "What are Master Numbers in Numerology?",
      a: "Master Numbers (11, 22, and 33) carry double-digit intensity and are not reduced to 2, 4, or 6 in Pythagorean calculations."
    },
    {
      q: "How is Name Numerology different from Life Path Numerology?",
      a: "Life Path Numerology uses your fixed date of birth, whereas Name Numerology assigns letter values to your full birth name."
    },
    {
      q: "Is this Numerology Calculator free?",
      a: "Yes, 100%! All calculations on AstroPredict are completely free."
    }
  ];

  const relatedCalculators = [
    {
      title: "Name Numerology Calculator",
      href: "/name-numerology-calculator",
      description: "Calculate your Destiny, Expression, and Soul Urge numbers from your full name.",
      categoryBadge: "Name"
    },
    {
      title: "Birth Chart Calculator",
      href: "/birth-chart-calculator",
      description: "Combine numerological insights with a full 12-house Vedic birth chart.",
      categoryBadge: "Vedic"
    },
    {
      title: "Moon Sign Calculator",
      href: "/moon-sign-calculator",
      description: "Discover your emotional baseline alongside your Life Path number.",
      categoryBadge: "Vedic"
    }
  ];

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-4">
      {/* Header Banner */}
      <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 sm:p-8 backdrop-blur-xl shadow-2xl relative overflow-hidden">
        <div className="flex items-center gap-2 text-[#5ab4ff] text-[11px] font-bold uppercase tracking-wider mb-2">
          <Sparkles className="w-4 h-4 text-[#5ab4ff]" />
          <span>Pythagorean Numerology</span>
        </div>
        <h1 className="text-3xl font-heading font-semibold text-white mb-2">
          Life Path &amp; Birthday Numerology Calculator
        </h1>
        <p className="text-[rgba(220,236,255,0.7)] text-sm max-w-2xl leading-relaxed">
          Discover your core Life Path Number and Birthday Number derived from your exact date of birth using classical Pythagorean reduction rules and Master Number (11, 22, 33) preservation.
        </p>
      </div>

      {/* Input Form */}
      <div className="bg-[rgba(5,10,36,0.92)] border border-white/16 rounded-3xl p-6 backdrop-blur-xl shadow-xl">
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-end gap-4">
          <div className="flex-1 w-full">
            <label htmlFor="dob-input" className="block text-[13px] font-medium text-[rgba(220,236,255,0.85)] mb-2">
              <Calendar className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-[#5ab4ff]" />
              Date of Birth
            </label>
            <input
              id="dob-input"
              type="date"
              required
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="w-full h-12 rounded-xl bg-white/[0.06] border border-white/25 px-4 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5ab4ff] transition-all [color-scheme:dark]"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:w-auto h-12 px-8 rounded-full bg-white text-[#03163d] font-semibold text-sm hover:bg-[#b9c6ff] shadow-[0_0_25px_rgba(150,165,255,0.4)] disabled:opacity-50 transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5ab4ff] active:scale-[0.98] shrink-0"
          >
            {loading ? 'Calculating...' : 'Calculate Life Path'}
          </button>
        </form>
        {error && <p className="mt-3 text-xs text-[#ff9bb0]">{error}</p>}
      </div>

      {/* Results Section */}
      {result && (
        <div className="space-y-6">
          {result.lifePathNumber && (
            <NumerologyResultCard label="Life Path Number" detail={result.lifePathNumber} />
          )}
          {result.birthdayNumber && (
            <NumerologyResultCard label="Birthday Number" detail={result.birthdayNumber} />
          )}
        </div>
      )}

      <CalculatorPageContent
        calculatorName="Numerology Calculator"
        introParagraph={introParagraph}
        methodologyTitle="How I Calculate Your Life Path & Birthday Numbers"
        methodologyContent={methodologyContent}
        subsections={subsections}
        faqs={faqs}
        relatedCalculators={relatedCalculators}
        showSignPeriodGrid={false}
      />
    </div>
  );
}
